import "./PendingChangesPanel.scss";
import { Header } from "../StickyTable";
import { MdClose } from "react-icons/md";
import { PendingChanges, RowType } from "./pendingChangesUtils";
import { StateSetter } from "../../utils/types";
import * as R from "ramda";
import PendingDeletedRow from "./PendingDeletedRow";
import PendingEditedRow from "./PendingEditedRow";
import PendingNewRow from "./PendingNewRow";
import React, { useMemo } from "react";

interface PendingChangesPanelProps<R> {
  headers: Header[];
  originalData?: Record<string, R>;
  pendingChanges: PendingChanges<R>;
  pendingRowHeaderRender?: (row: R) => JSX.Element;
  setShowPendingChanges: StateSetter<boolean>;
  showPendingChanges: boolean;
}

export const PendingChangesPanel = <R extends RowType>({
  headers,
  originalData,
  pendingChanges,
  pendingRowHeaderRender,
  setShowPendingChanges,
  showPendingChanges,
}: PendingChangesPanelProps<R>): JSX.Element => {
  const { editedRows, newRows, deletedRows } = pendingChanges;
  const moreThanOneTypeOfPendingChanges =
    (R.defaultTo([], editedRows).length > 0
      ? 1
      : 0 + R.defaultTo([], newRows).length > 0
      ? 1
      : 0 + R.defaultTo([], deletedRows).length > 0
      ? 1
      : 0) > 1;

  const hasPendingChanges = useMemo(
    () => !R.isEmpty(editedRows) || !R.isEmpty(newRows) || !R.isEmpty(deletedRows),
    [editedRows, newRows, deletedRows]
  );

  return showPendingChanges && hasPendingChanges ? (
    <div className="pendingChangesPanel">
      <div className="pendingChangesTop">
        Pending Changes
        <div className="closeButton" onClick={() => setShowPendingChanges(false)}>
          <MdClose />
        </div>
      </div>
      <div className="pendingChangesBody">
        {editedRows && !R.isEmpty(editedRows) && originalData && (
          <div className="body">
            {moreThanOneTypeOfPendingChanges && (
              <span className="editsSectionTitle">Edited Rows</span>
            )}
            <div className="editsList">
              {editedRows.map(editedRow => {
                return (
                  <PendingEditedRow
                    columns={headers}
                    key={JSON.stringify(editedRow)}
                    newRow={editedRow}
                    oldRow={originalData[editedRow.id]}
                    pendingRowHeaderRender={pendingRowHeaderRender}
                  />
                );
              })}
            </div>
          </div>
        )}
        {newRows && !R.isEmpty(newRows) && (
          <div className="body">
            {moreThanOneTypeOfPendingChanges && <span className="editsSectionTitle">New Rows</span>}
            <div className="editsList">
              {newRows.map(newRow => {
                return (
                  <PendingNewRow
                    columns={headers}
                    key={JSON.stringify(newRow)}
                    newRow={newRow}
                    pendingRowHeaderRender={pendingRowHeaderRender}
                  />
                );
              })}
            </div>
          </div>
        )}
        {deletedRows && !R.isEmpty(deletedRows) && (
          <div className="body">
            {moreThanOneTypeOfPendingChanges && (
              <span className="editsSectionTitle">Deleted Rows</span>
            )}
            <div className="editsList">
              {deletedRows.map(deletedRow => {
                return (
                  <PendingDeletedRow
                    columns={headers}
                    key={JSON.stringify(deletedRow)}
                    oldRow={deletedRow}
                    pendingRowHeaderRender={pendingRowHeaderRender}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  ) : (
    <div></div>
  );
};
